import { MIN_STAKE } from '@star-snake/shared'
import { NeonColors, FONTS, neonTitleGlow, fadeTransition, telegramStarIcon } from '../neon-theme'
import { NetworkManager } from '../../network/NetworkManager'
import { mountToAppShell } from '../appShell'
import { Marquee } from '../components/Marquee'
import { KingMessageModal } from '../dialogs/KingMessageModal'
import { LeaderboardScreen, type TodayLeaderboardEntry } from './LeaderboardScreen'
import { TonPreviewScreen } from './TonPreviewScreen'
import { ProfileScreen } from './ProfileScreen'
import { DailyRewardsScreen } from './DailyRewardsScreen'
import { buildIdentityLine } from './mainMenu/identityLine'
import { buildPlayButtonModel, type PlayButtonModel } from './mainMenu/playButtonState'
import { buildStatusLine } from './mainMenu/statusLine'
import { gateKingMarquee } from './mainMenu/kingMarqueeGate'

/**
 * SNAKE-30: minimalist main menu — six functional blocks total:
 *
 *   1. Header bar           — ⚙ left, identity+balance right (safe-area-inset-top)
 *   2. King marquee         — only when there's a custom king message
 *   3. Logo                 — Star Snake (no subtitle)
 *   4. PLAY button          — 5-state machine, single visual accent
 *   5. Smart status line    — top-10/ranked/CTA/hidden → tap opens leaderboard
 *   6. Bottom row           — 🎁 Daily + 💎 TON only (safe-area-inset-bottom)
 *
 * What WAS on the main menu and moved elsewhere (still accessible):
 *   - balance badge, wallet popup, mode tabs (Рейтинг/TON), leaderboard
 *     collapse, countdown, "Collect. Survive. Evacuate." subtitle, name input,
 *     snake preview, secondary Profile button, settings gear in corner,
 *     build/version text. Most live in ProfileScreen tabs now.
 *
 * Public API kept stable for main.ts: show({onPlay, onSettings, onAdmin?, ...}),
 * setJoining(), refreshFromNetwork(), isVisible(), hide(), dispose().
 */

const NAME_STORAGE_KEY = 'star-snake-player-name'
const WELCOME_TOAST_FLAG = 'welcomeShown'

const PREVIEW_POLL_BASE_MS = 30_000
const PREVIEW_POLL_JITTER_MS = 6_000
// King-of-the-hill poll runs slower and offset from the leaderboard poll so the
// two endpoints don't fire concurrently every cycle.
const KING_POLL_BASE_MS = 45_000
const KING_POLL_JITTER_MS = 9_000

const SNAKE_NAMES = [
  'Cobra', 'Viper', 'Sidewinder', 'Anaconda', 'Python',
  'Mamba', 'Rattler', 'Kingsnake', 'Copperhead', 'Taipan',
  'Asp', 'Boa', 'Racer', 'Krait', 'Adder',
]

function randomSnakeName(): string {
  return SNAKE_NAMES[Math.floor(Math.random() * SNAKE_NAMES.length)]
}

interface MainMenuCallbacks {
  onPlay: (stake: number, playerName: string) => void
  onSettings: () => void
  onAdmin?: () => void
  balance: number
  freeGamesLeft: number
  defaultName: string
}

export class MainMenuScreen {
  private container: HTMLDivElement
  private callbacks: MainMenuCallbacks | null = null
  private hideTimerId: number | null = null

  // DOM refs that need to be re-rendered when network state changes.
  private playBtn: HTMLButtonElement | null = null
  private playUnderlineEl: HTMLDivElement | null = null
  private playGiftCornerEl: HTMLSpanElement | null = null
  private statusLineEl: HTMLButtonElement | null = null
  private identityBtn: HTMLButtonElement | null = null
  private dailyBtn: HTMLButtonElement | null = null
  private dailyHintEl: HTMLDivElement | null = null

  // Sub-screens (lifetime owned by this screen).
  private leaderboardScreen: LeaderboardScreen | null = null
  private tonPreviewScreen: TonPreviewScreen | null = null
  private profileScreen: ProfileScreen | null = null
  private dailyRewardsScreen: DailyRewardsScreen | null = null

  // King-of-the-hill marquee + modal (SNAKE-15).
  private kingMarquee: Marquee | null = null
  private kingModal: KingMessageModal | null = null
  private kingPollTimer: number | null = null
  private kingPromptShown = false

  // Daily leaderboard bootstrap polling.
  private bootstrapPollTimer: number | null = null
  private visibilityHandler: (() => void) | null = null

  // Local view of last-known bootstrap data, so we can rebuild the status
  // line and PLAY model whenever the network balance changes.
  private dailyAvailable = false
  private hasPlayedToday = false
  private myDailyRank: { rank: number; score: number } | null = null
  private leaderboardHasEntries = false
  private bootstrapLoaded = false

  private joining = false

  private hiddenAdminTapCount = 0
  private hiddenAdminTapResetHandle: number | null = null

  constructor() {
    this.container = document.createElement('div')
    this.container.id = 'main-menu'
    this.container.style.cssText = `
      position: absolute; top: 0; left: 0; width: 100%; height: 100%;
      background:
        radial-gradient(ellipse at top, rgba(99,102,241,0.10), transparent 60%),
        radial-gradient(ellipse at bottom, rgba(236,72,153,0.06), transparent 50%),
        ${NeonColors.bgDark};
      color: ${NeonColors.textBright}; font-family: ${FONTS.body};
      display: flex; flex-direction: column;
      overflow: hidden;
      opacity: 0; ${fadeTransition(300)}
    `
  }

  show(callbacks: MainMenuCallbacks): void {
    if (this.hideTimerId !== null) {
      window.clearTimeout(this.hideTimerId)
      this.hideTimerId = null
    }
    this.joining = false
    this.callbacks = callbacks

    // Resolve player name (stored > Telegram username > random) — name input
    // now lives in Profile/Settings tab; this is just the cached value we ship
    // to onPlay().
    const storedName = typeof localStorage !== 'undefined' ? localStorage.getItem(NAME_STORAGE_KEY) : null
    const playerName = storedName || callbacks.defaultName || randomSnakeName()
    if (typeof localStorage !== 'undefined' && !storedName && playerName) {
      localStorage.setItem(NAME_STORAGE_KEY, playerName)
    }

    this.teardownMountedComponents()
    while (this.container.firstChild) this.container.removeChild(this.container.firstChild)
    this.container.appendChild(this.buildSparseStars())

    const content = document.createElement('div')
    content.style.cssText = `
      position: relative; z-index: 1;
      display: flex; flex-direction: column;
      width: 100%; height: 100%;
      padding-top: env(safe-area-inset-top, 0px);
      padding-bottom: env(safe-area-inset-bottom, 16px);
      box-sizing: border-box;
    `

    content.appendChild(this.buildHeader())
    content.appendChild(this.buildMarqueeBlock())

    const middle = document.createElement('div')
    middle.style.cssText = `
      flex: 1 1 auto; display: flex; flex-direction: column;
      align-items: center; justify-content: center;
      padding: 12px 16px;
      gap: 24px;
    `
    middle.appendChild(this.buildLogo())
    middle.appendChild(this.buildPlayBlock())
    middle.appendChild(this.buildStatusLine())
    content.appendChild(middle)

    content.appendChild(this.buildBottomRow())

    this.container.appendChild(content)
    mountToAppShell(this.container)
    requestAnimationFrame(() => { this.container.style.opacity = '1' })

    this.refreshFromNetwork()
    this.installVisibilityHandler()
    void this.fetchMenuBootstrap()
    void this.fetchKingMessage()
    void this.maybeShowWelcomeToast(callbacks.freeGamesLeft)
  }

  hide(): void {
    if (this.hideTimerId !== null) {
      window.clearTimeout(this.hideTimerId)
      this.hideTimerId = null
    }
    // Kill background work immediately so polls don't touch refs while the
    // menu is fading out. Sub-screens stay alive for the 300 ms fade so a
    // visible LeaderboardScreen / ProfileScreen / TonPreviewScreen continues
    // rendering on top of the menu until it disappears; they're disposed
    // inside the timeout so the menu isn't half-dead between calls.
    this.clearBootstrapPollTimer()
    this.clearKingPollTimer()
    this.removeVisibilityHandler()
    this.container.style.opacity = '0'
    this.hideTimerId = window.setTimeout(() => {
      this.disposeSubScreens()
      this.container.remove()
      this.hideTimerId = null
      this.clearRefs()
    }, 300)
  }

  dispose(): void {
    if (this.hideTimerId !== null) {
      window.clearTimeout(this.hideTimerId)
      this.hideTimerId = null
    }
    this.clearBootstrapPollTimer()
    this.clearKingPollTimer()
    this.removeVisibilityHandler()
    this.disposeSubScreens()
    this.container.remove()
    this.clearRefs()
  }

  isVisible(): boolean {
    return document.body.contains(this.container)
  }

  setJoining(joining: boolean): void {
    this.joining = joining
    this.applyPlayState()
  }

  refreshFromNetwork(): void {
    const network = NetworkManager.getInstance()
    this.refreshIdentity(network.balance, network.freeGamesLeft, network.defaultDisplayName)
    this.applyPlayState()
  }

  // ===========================================================================
  // Header — ⚙ left, identity+balance right (kept in safe-area-inset-top)
  // ===========================================================================

  private buildHeader(): HTMLElement {
    const bar = document.createElement('div')
    bar.style.cssText = `
      display: flex; align-items: center; justify-content: space-between;
      padding: 8px 14px;
      min-height: 56px;
      gap: 12px;
    `

    const settingsBtn = document.createElement('button')
    settingsBtn.type = 'button'
    settingsBtn.setAttribute('aria-label', 'Настройки')
    settingsBtn.style.cssText = `
      width: 44px; height: 44px;
      display: flex; align-items: center; justify-content: center;
      background: rgba(255,255,255,0.04);
      border: 1px solid rgba(255,255,255,0.10);
      border-radius: 50%;
      color: ${NeonColors.textMuted};
      font-size: 1.3rem; cursor: pointer;
      font-family: ${FONTS.body};
    `
    settingsBtn.textContent = '⚙'
    settingsBtn.addEventListener('click', () => this.callbacks?.onSettings())
    bar.appendChild(settingsBtn)

    const identity = document.createElement('button')
    identity.type = 'button'
    identity.style.cssText = `
      min-height: 44px;
      display: flex; align-items: center; gap: 6px;
      padding: 0 14px;
      background: rgba(255,255,255,0.04);
      border: 1px solid rgba(255,255,255,0.10);
      border-radius: 22px;
      color: ${NeonColors.textBright};
      font-size: 0.9rem; font-weight: 600;
      cursor: pointer; font-family: ${FONTS.body};
      max-width: 70%;
      overflow: hidden; white-space: nowrap; text-overflow: ellipsis;
    `
    identity.addEventListener('click', () => this.openProfile('stats'))
    identity.addEventListener('contextmenu', (e) => {
      e.preventDefault()
      this.handleHiddenAdminTap()
    })
    this.identityBtn = identity

    bar.appendChild(identity)
    return bar
  }

  private refreshIdentity(balance: number, freeGamesLeft: number, displayName: string): void {
    if (!this.identityBtn) return
    const line = buildIdentityLine({ displayName, balance, freeGamesLeft })
    this.identityBtn.textContent = line.text
  }

  // ===========================================================================
  // King marquee — hidden by default; populated only on a real custom message
  // ===========================================================================

  private buildMarqueeBlock(): HTMLElement {
    const wrap = document.createElement('div')
    wrap.style.cssText = 'padding: 0 14px; margin: 4px 0;'

    this.kingMarquee = new Marquee()
    this.kingMarquee.mount(wrap)
    // Empty by default → Marquee.setText('') hides the container.
    this.kingMarquee.setText('')

    return wrap
  }

  // ===========================================================================
  // Logo
  // ===========================================================================

  private buildLogo(): HTMLElement {
    const logo = document.createElement('h1')
    logo.style.cssText = `
      ${neonTitleGlow()}
      font-size: clamp(2.4rem, 11vw, 4.2rem);
      letter-spacing: -0.02em; line-height: 0.85;
      margin: 0; text-align: center;
      animation: star-snake-logo-glow 4s ease-in-out infinite;
    `
    logo.innerHTML = '<span>Star</span><br><span>Snake</span>'
    logo.addEventListener('click', () => this.handleHiddenAdminTap())
    logo.addEventListener('touchstart', () => this.handleHiddenAdminTap(), { passive: true })

    ensureMenuKeyframes()
    return logo
  }

  // ===========================================================================
  // PLAY block — button + underline copy
  // ===========================================================================

  private buildPlayBlock(): HTMLElement {
    const wrap = document.createElement('div')
    wrap.style.cssText = `
      width: 100%; max-width: 360px;
      display: flex; flex-direction: column; align-items: center; gap: 8px;
    `

    const btn = document.createElement('button')
    btn.type = 'button'
    btn.style.cssText = `
      width: 100%; min-height: 72px;
      padding: 14px 20px;
      border: none; border-radius: 18px;
      background: ${NeonColors.primary};
      color: #0f172a;
      font-family: ${FONTS.body};
      font-size: 1.3rem; font-weight: 900;
      letter-spacing: 0.12em; text-transform: uppercase;
      cursor: pointer; position: relative;
      box-shadow: 0 0 30px rgba(251,191,36,0.32);
      animation: star-snake-play-breath 2s ease-in-out infinite;
      display: flex; flex-direction: column; align-items: center; gap: 4px;
    `
    btn.addEventListener('click', () => this.onPlayTap())

    const corner = document.createElement('span')
    corner.style.cssText = `
      position: absolute; top: -8px; right: -8px;
      width: 32px; height: 32px;
      border-radius: 50%;
      background: ${NeonColors.success}; color: #0f172a;
      display: none; align-items: center; justify-content: center;
      font-size: 1.2rem;
      box-shadow: 0 0 16px rgba(0,255,136,0.6);
    `
    corner.textContent = '🎁'
    btn.appendChild(corner)
    this.playGiftCornerEl = corner

    this.playBtn = btn
    wrap.appendChild(btn)

    const underline = document.createElement('div')
    underline.style.cssText = `
      min-height: 18px; font-size: 0.85rem;
      color: ${NeonColors.textMuted};
      text-align: center;
    `
    this.playUnderlineEl = underline
    wrap.appendChild(underline)

    return wrap
  }

  private onPlayTap(): void {
    if (!this.callbacks || this.joining) return
    const network = NetworkManager.getInstance()
    const model = buildPlayButtonModel({
      balance: network.balance,
      freeGamesLeft: network.freeGamesLeft,
      dailyAvailable: this.dailyAvailable,
      joining: this.joining,
    })

    triggerHapticMedium()

    if (model.action === 'deposit') {
      // Direct path to wallet deposit (Profile → Wallet tab).
      this.openProfile('wallet')
      return
    }

    if (model.action === 'claim_daily_then_play') {
      // Daily Rewards screen autoclaims (the screen's own claim button fires on mount)
      // — keep this as a clear, single tap into the rewards UX rather than a hidden
      // race-condition autoclaim in the background.
      this.openDailyRewards()
      return
    }

    // Default: play.
    const rawName = (typeof localStorage !== 'undefined' ? localStorage.getItem(NAME_STORAGE_KEY) : null)
      ?? this.callbacks.defaultName
      ?? randomSnakeName()
    const name = rawName.replace(/<[^>]*>/g, '').slice(0, 20)
    this.callbacks.onPlay(MIN_STAKE, name)
  }

  private applyPlayState(): void {
    if (!this.playBtn || !this.playUnderlineEl || !this.playGiftCornerEl) return
    const network = NetworkManager.getInstance()
    const model = buildPlayButtonModel({
      balance: network.balance,
      freeGamesLeft: network.freeGamesLeft,
      dailyAvailable: this.dailyAvailable,
      joining: this.joining,
    })
    renderPlayButton(this.playBtn, this.playUnderlineEl, this.playGiftCornerEl, model)
    // Status line depends on dailyAvailable indirectly (we re-build on any flip).
    this.refreshStatusLine()
  }

  // ===========================================================================
  // Smart status line — tap → leaderboard
  // ===========================================================================

  private buildStatusLine(): HTMLElement {
    const el = document.createElement('button')
    el.type = 'button'
    el.style.cssText = `
      background: transparent; border: none;
      color: ${NeonColors.textMuted};
      font-family: ${FONTS.body}; font-size: 0.85rem;
      cursor: pointer; padding: 6px 10px;
      letter-spacing: 0.01em;
      display: none;
    `
    el.addEventListener('click', () => this.openLeaderboard())
    this.statusLineEl = el
    return el
  }

  private refreshStatusLine(): void {
    if (!this.statusLineEl) return
    const line = buildStatusLine({
      myDailyRank: this.myDailyRank,
      hasPlayedToday: this.hasPlayedToday,
      leaderboardHasEntries: this.leaderboardHasEntries,
      bootstrapLoaded: this.bootstrapLoaded,
    })
    if (line.kind === 'hidden') {
      this.statusLineEl.style.display = 'none'
      this.statusLineEl.textContent = ''
    } else {
      this.statusLineEl.style.display = 'block'
      this.statusLineEl.textContent = line.text
      this.statusLineEl.style.color = line.kind === 'top10' ? NeonColors.primary : NeonColors.textMuted
    }
  }

  // ===========================================================================
  // Bottom row — 🎁 Daily + 💎 TON
  // ===========================================================================

  private buildBottomRow(): HTMLElement {
    const row = document.createElement('div')
    row.style.cssText = `
      display: flex; gap: 32px; justify-content: center; align-items: flex-end;
      padding: 12px 16px 16px;
    `

    const daily = this.buildBottomAction({
      emoji: '🎁', label: 'Daily',
      onTap: () => this.openDailyRewards(),
    })
    this.dailyBtn = daily.button
    this.dailyHintEl = daily.hintEl
    row.appendChild(daily.wrap)

    const ton = this.buildBottomAction({
      emoji: '💎', label: 'TON',
      onTap: () => this.openTonPreview(),
      hint: 'Скоро',
    })
    row.appendChild(ton.wrap)

    return row
  }

  private buildBottomAction(opts: {
    emoji: string
    label: string
    onTap: () => void
    hint?: string
  }): { wrap: HTMLElement; button: HTMLButtonElement; hintEl: HTMLDivElement } {
    const wrap = document.createElement('div')
    wrap.style.cssText = 'display:flex; flex-direction:column; align-items:center; gap:4px;'

    const btn = document.createElement('button')
    btn.type = 'button'
    btn.setAttribute('aria-label', opts.label)
    btn.style.cssText = `
      width: 48px; height: 48px;
      display: flex; align-items: center; justify-content: center;
      background: rgba(0,2,10,0.7);
      border: 1px solid rgba(255,255,255,0.10);
      border-radius: 14px;
      font-size: 1.4rem; cursor: pointer;
      font-family: ${FONTS.body};
      transition: border-color 0.15s, box-shadow 0.15s;
    `
    btn.textContent = opts.emoji
    btn.addEventListener('click', opts.onTap)
    wrap.appendChild(btn)

    const label = document.createElement('div')
    label.style.cssText = `font-size: 10px; color: ${NeonColors.textMuted}; letter-spacing: 0.1em;`
    label.textContent = opts.label
    wrap.appendChild(label)

    const hint = document.createElement('div')
    hint.style.cssText = `font-size: 10px; color: ${NeonColors.textDim}; min-height: 12px;`
    hint.textContent = opts.hint ?? ''
    wrap.appendChild(hint)

    return { wrap, button: btn, hintEl: hint }
  }

  private applyDailyButtonState(): void {
    if (!this.dailyBtn || !this.dailyHintEl) return
    if (this.dailyAvailable) {
      this.dailyBtn.style.boxShadow = '0 0 18px rgba(0,255,136,0.55)'
      this.dailyBtn.style.borderColor = 'rgba(0,255,136,0.5)'
      this.dailyBtn.style.animation = 'star-snake-daily-pulse 1.6s ease-in-out infinite'
      this.dailyHintEl.textContent = 'Готово'
      this.dailyHintEl.style.color = NeonColors.success
    } else {
      this.dailyBtn.style.boxShadow = 'none'
      this.dailyBtn.style.borderColor = 'rgba(255,255,255,0.10)'
      this.dailyBtn.style.animation = 'none'
      this.dailyHintEl.textContent = ''
      this.dailyHintEl.style.color = NeonColors.textDim
    }
  }

  // ===========================================================================
  // Background — sparse stars only, no nebula, no blur
  // ===========================================================================

  private buildSparseStars(): HTMLElement {
    const wrap = document.createElement('div')
    wrap.style.cssText = `
      position: absolute; inset: 0; pointer-events: none;
      background-image:
        radial-gradient(1px 1px at 18% 22%, rgba(255,255,255,0.55), transparent),
        radial-gradient(1.5px 1.5px at 72% 14%, rgba(255,255,255,0.6), transparent),
        radial-gradient(1px 1px at 30% 78%, rgba(255,255,255,0.45), transparent),
        radial-gradient(1.5px 1.5px at 86% 64%, rgba(255,255,255,0.55), transparent),
        radial-gradient(1px 1px at 55% 50%, rgba(255,255,255,0.4), transparent),
        radial-gradient(1px 1px at 8% 60%, rgba(255,255,255,0.35), transparent),
        radial-gradient(1.5px 1.5px at 92% 28%, rgba(255,255,255,0.5), transparent),
        radial-gradient(1px 1px at 42% 12%, rgba(255,255,255,0.4), transparent),
        radial-gradient(1px 1px at 64% 86%, rgba(255,255,255,0.4), transparent);
    `
    return wrap
  }

  // ===========================================================================
  // Daily / leaderboard bootstrap (lightweight — single endpoint)
  // ===========================================================================

  private async fetchMenuBootstrap(): Promise<void> {
    const network = NetworkManager.getInstance()
    const token = network.getToken()
    if (!token) {
      this.scheduleBootstrapPoll()
      return
    }

    try {
      const res = await fetch('/api/menu/bootstrap', {
        headers: { Authorization: `Bearer ${token}` },
      })
      if (!res.ok) throw new Error(`HTTP ${res.status}`)
      const data = await res.json()
      if (data?.success && data?.data) {
        const top = Array.isArray(data.data.topToday)
          ? (data.data.topToday as TodayLeaderboardEntry[])
          : []
        const myRank = data.data.myDailyRank as { rank: number; score: number } | null | undefined
        const hasPlayed = data.data.hasPlayedToday === true
        const dailyReward = data.data.dailyReward as { canClaim: boolean } | null | undefined

        this.dailyAvailable = dailyReward?.canClaim === true
        this.hasPlayedToday = hasPlayed
        this.myDailyRank = myRank ?? null
        this.leaderboardHasEntries = top.length > 0 || myRank !== null
        this.bootstrapLoaded = true
        this.applyDailyButtonState()
        this.applyPlayState()
      }
    } catch (err) {
      console.warn('[MainMenu] menu/bootstrap fetch failed:', err)
    } finally {
      this.scheduleBootstrapPoll()
    }
  }

  private scheduleBootstrapPoll(): void {
    this.clearBootstrapPollTimer()
    if (!this.isVisible()) return
    if (typeof document !== 'undefined' && document.visibilityState !== 'visible') return
    const jitter = (Math.random() * 2 - 1) * PREVIEW_POLL_JITTER_MS
    const delay = PREVIEW_POLL_BASE_MS + jitter
    this.bootstrapPollTimer = window.setTimeout(() => {
      this.bootstrapPollTimer = null
      if (!this.isVisible()) return
      if (typeof document !== 'undefined' && document.visibilityState !== 'visible') return
      void this.fetchMenuBootstrap()
    }, delay)
  }

  private clearBootstrapPollTimer(): void {
    if (this.bootstrapPollTimer !== null) {
      window.clearTimeout(this.bootstrapPollTimer)
      this.bootstrapPollTimer = null
    }
  }

  // ===========================================================================
  // King-of-the-hill marquee — gate on real custom message
  // ===========================================================================

  private async fetchKingMessage(): Promise<void> {
    const network = NetworkManager.getInstance()
    const token = network.getToken()

    try {
      const tasks: Array<Promise<Response>> = [fetch('/api/king-message/current')]
      if (token) {
        tasks.push(fetch('/api/king-message/me', { headers: { Authorization: `Bearer ${token}` } }))
      }
      const results = await Promise.allSettled(tasks)

      const currentRes = results[0]
      if (currentRes.status === 'fulfilled' && currentRes.value.ok) {
        try {
          const data = await currentRes.value.json()
          const message: string | null | undefined = data?.success ? data?.data?.message : null
          const gate = gateKingMarquee(message)
          if (this.kingMarquee) {
            this.kingMarquee.setText(gate.shouldShow ? gate.text : '')
          }
        } catch {
          // ignore parse — leave marquee in previous state
        }
      } else if (this.kingMarquee) {
        // Network/HTTP error → hide marquee. No placeholder.
        this.kingMarquee.setText('')
      }

      if (token && results.length > 1) {
        const meRes = results[1]
        if (meRes.status === 'fulfilled' && meRes.value.ok) {
          try {
            const data = await meRes.value.json()
            if (data?.success && data?.data?.pending === true && !this.kingPromptShown && !this.kingModal) {
              const claimId = String(data.data.claimId ?? '')
              const previousMessage = typeof data.data.previousMessage === 'string'
                ? data.data.previousMessage
                : undefined
              if (claimId) this.openKingModal(claimId, previousMessage)
            }
          } catch { /* swallow */ }
        }
      }
    } catch {
      // network error — silent
    } finally {
      this.scheduleKingPoll()
    }
  }

  private openKingModal(claimId: string, previousMessage: string | undefined): void {
    if (this.kingModal) return
    this.kingPromptShown = true
    const modal = new KingMessageModal({
      claimId,
      previousMessage,
      onSubmitted: () => void this.fetchKingMessage(),
      onClose: () => { this.kingModal = null },
    })
    this.kingModal = modal
    modal.show()
  }

  private scheduleKingPoll(): void {
    this.clearKingPollTimer()
    if (!this.kingMarquee) return
    if (typeof document !== 'undefined' && document.visibilityState !== 'visible') return
    const jitter = (Math.random() * 2 - 1) * KING_POLL_JITTER_MS
    const delay = KING_POLL_BASE_MS + jitter
    this.kingPollTimer = window.setTimeout(() => {
      this.kingPollTimer = null
      if (!this.kingMarquee) return
      if (typeof document !== 'undefined' && document.visibilityState !== 'visible') return
      void this.fetchKingMessage()
    }, delay)
  }

  private clearKingPollTimer(): void {
    if (this.kingPollTimer !== null) {
      window.clearTimeout(this.kingPollTimer)
      this.kingPollTimer = null
    }
  }

  private installVisibilityHandler(): void {
    if (typeof document === 'undefined') return
    if (this.visibilityHandler) return
    const handler = (): void => {
      if (document.visibilityState === 'visible') {
        if (this.bootstrapPollTimer === null) this.scheduleBootstrapPoll()
        if (this.kingMarquee && this.kingPollTimer === null) this.scheduleKingPoll()
      } else {
        this.clearBootstrapPollTimer()
        this.clearKingPollTimer()
      }
    }
    this.visibilityHandler = handler
    document.addEventListener('visibilitychange', handler)
  }

  private removeVisibilityHandler(): void {
    if (this.visibilityHandler && typeof document !== 'undefined') {
      document.removeEventListener('visibilitychange', this.visibilityHandler)
    }
    this.visibilityHandler = null
  }

  // ===========================================================================
  // Sub-screen entry points
  // ===========================================================================

  private openLeaderboard(): void {
    if (this.leaderboardScreen) return
    const screen = new LeaderboardScreen()
    this.leaderboardScreen = screen
    screen.show(() => { this.leaderboardScreen = null })
  }

  private openTonPreview(): void {
    if (this.tonPreviewScreen) return
    const screen = new TonPreviewScreen()
    this.tonPreviewScreen = screen
    screen.show(() => { this.tonPreviewScreen = null })
  }

  private openProfile(initialTab: 'stats' | 'wallet' | 'settings' = 'stats'): void {
    if (this.profileScreen) return
    const screen = new ProfileScreen()
    this.profileScreen = screen
    screen.show(() => {
      this.profileScreen = null
      this.refreshFromNetwork()
    }, { initialTab })
  }

  private openDailyRewards(): void {
    if (this.dailyRewardsScreen) return
    const screen = new DailyRewardsScreen()
    this.dailyRewardsScreen = screen
    screen.show(
      () => { this.dailyRewardsScreen = null },
      {
        onClaimed: () => {
          void this.refreshAfterDailyClaim()
          this.showToast('🎁 Бонус получен!', 'success')
        },
      },
    )
  }

  private async refreshAfterDailyClaim(): Promise<void> {
    const network = NetworkManager.getInstance()
    await network.fetchBalance()
    this.refreshFromNetwork()
    await this.fetchMenuBootstrap()
  }

  private disposeSubScreens(): void {
    if (this.leaderboardScreen) { this.leaderboardScreen.dispose(); this.leaderboardScreen = null }
    if (this.tonPreviewScreen) { this.tonPreviewScreen.dispose(); this.tonPreviewScreen = null }
    if (this.profileScreen) { this.profileScreen.dispose(); this.profileScreen = null }
    if (this.dailyRewardsScreen) { this.dailyRewardsScreen.dispose(); this.dailyRewardsScreen = null }
    if (this.kingModal) { this.kingModal.destroy(); this.kingModal = null }
    if (this.kingMarquee) { this.kingMarquee.destroy(); this.kingMarquee = null }
  }

  private teardownMountedComponents(): void {
    this.clearBootstrapPollTimer()
    this.clearKingPollTimer()
    this.disposeSubScreens()
    this.playBtn = null
    this.playUnderlineEl = null
    this.playGiftCornerEl = null
    this.statusLineEl = null
    this.identityBtn = null
    this.dailyBtn = null
    this.dailyHintEl = null
  }

  // ===========================================================================
  // Welcome toast + shared toast
  // ===========================================================================

  private async maybeShowWelcomeToast(freeGamesLeft: number): Promise<void> {
    try {
      if (typeof localStorage === 'undefined') return
      if (localStorage.getItem(WELCOME_TOAST_FLAG) === '1') return
      if (freeGamesLeft <= 0) return

      const network = NetworkManager.getInstance()
      const token = network.getToken()
      if (!token) return

      const res = await fetch('/api/profile/stats', { headers: { Authorization: `Bearer ${token}` } })
      if (!res.ok) return
      const json = await res.json() as { success?: boolean; data?: { gamesPlayed?: number } }
      if (!json?.success || !json.data) return
      if ((json.data.gamesPlayed ?? -1) !== 0) return

      if (localStorage.getItem(WELCOME_TOAST_FLAG) === '1') return
      if (!this.isVisible()) return

      this.showToast(`🎁 Тебе подарено ${freeGamesLeft} бесплатных игр!`, 'success')
      localStorage.setItem(WELCOME_TOAST_FLAG, '1')
    } catch {
      // toast is non-critical
    }
  }

  private showToast(message: string, type: 'success' | 'error'): void {
    const color = type === 'success' ? NeonColors.success : NeonColors.danger
    const toast = document.createElement('div')
    toast.style.cssText = `
      position: absolute; top: 16px; left: 50%; transform: translateX(-50%);
      background: ${color}; color: #fff; padding: 10px 20px;
      border-radius: 12px; font-size: 0.9rem; z-index: 10000;
      box-shadow: 0 0 20px ${color}40;
      transition: opacity 0.3s;
      font-family: ${FONTS.body};
    `
    toast.textContent = message
    mountToAppShell(toast)
    window.setTimeout(() => {
      toast.style.opacity = '0'
      window.setTimeout(() => toast.remove(), 300)
    }, 3000)
  }

  private clearRefs(): void {
    this.callbacks = null
    this.joining = false
    this.dailyAvailable = false
    this.hasPlayedToday = false
    this.myDailyRank = null
    this.leaderboardHasEntries = false
    this.bootstrapLoaded = false
    this.playBtn = null
    this.playUnderlineEl = null
    this.playGiftCornerEl = null
    this.statusLineEl = null
    this.identityBtn = null
    this.dailyBtn = null
    this.dailyHintEl = null
    this.kingMarquee = null
    this.kingModal = null
    this.kingPollTimer = null
    this.kingPromptShown = false
    this.leaderboardScreen = null
    this.tonPreviewScreen = null
    this.profileScreen = null
    this.dailyRewardsScreen = null
    this.bootstrapPollTimer = null
    this.hiddenAdminTapCount = 0
    if (this.hiddenAdminTapResetHandle !== null) {
      window.clearTimeout(this.hiddenAdminTapResetHandle)
      this.hiddenAdminTapResetHandle = null
    }
  }

  // ===========================================================================
  // Hidden admin tap (5 taps on logo / right-click identity)
  // ===========================================================================

  private handleHiddenAdminTap(): void {
    this.hiddenAdminTapCount += 1
    if (this.hiddenAdminTapResetHandle !== null) {
      window.clearTimeout(this.hiddenAdminTapResetHandle)
    }
    this.hiddenAdminTapResetHandle = window.setTimeout(() => {
      this.hiddenAdminTapCount = 0
      this.hiddenAdminTapResetHandle = null
    }, 1500)
    if (this.hiddenAdminTapCount >= 5) {
      this.hiddenAdminTapCount = 0
      if (this.hiddenAdminTapResetHandle !== null) {
        window.clearTimeout(this.hiddenAdminTapResetHandle)
        this.hiddenAdminTapResetHandle = null
      }
      this.callbacks?.onAdmin?.()
    }
  }
}

// ============================================================================
// Renderers / helpers (kept outside the class so future tests can target them)
// ============================================================================

function renderPlayButton(
  btn: HTMLButtonElement,
  underline: HTMLDivElement,
  giftCorner: HTMLSpanElement,
  model: PlayButtonModel,
): void {
  // Detach the giftCorner so we can fully clear the button, then re-attach it
  // at the end. The corner is a fixed long-lived child (position:absolute) so
  // we never throw it away — that would lose listeners/state if any get added.
  if (giftCorner.parentElement === btn) btn.removeChild(giftCorner)
  while (btn.firstChild) btn.removeChild(btn.firstChild)

  const label = document.createElement('span')
  label.style.cssText = 'font-size: 1.4rem; font-weight: 900; letter-spacing: 0.12em;'
  label.textContent = model.primaryLabel
  btn.appendChild(label)

  if (model.badgeText !== null) {
    const badge = document.createElement('span')
    badge.style.cssText = `
      font-size: 0.8rem; font-weight: 700;
      background: rgba(15,23,42,0.18);
      padding: 4px 10px; border-radius: 8px;
      display: inline-flex; align-items: center; gap: 4px;
    `
    const text = document.createElement('span')
    // Trim trailing space — the layout uses flex `gap`, so the literal string
    // never needs cosmetic spacing.
    text.textContent = model.badgeText.trimEnd()
    badge.appendChild(text)
    if (model.badgeAppendsStar) {
      badge.appendChild(getCachedStarIconClone())
    }
    btn.appendChild(badge)
  }

  btn.appendChild(giftCorner)
  giftCorner.style.display = model.showGiftCorner ? 'flex' : 'none'

  btn.disabled = model.disabled
  btn.style.cursor = model.disabled ? 'not-allowed' : 'pointer'
  btn.style.opacity = model.disabled ? '0.7' : '1'
  if (model.variant === 'success') {
    btn.style.background = NeonColors.success
    btn.style.color = '#0f172a'
    btn.style.boxShadow = '0 0 30px rgba(0,255,136,0.32)'
  } else {
    btn.style.background = NeonColors.primary
    btn.style.color = '#0f172a'
    btn.style.boxShadow = '0 0 30px rgba(251,191,36,0.32)'
  }

  underline.textContent = model.underline
}

/**
 * Cache the parsed Telegram-star SVG so applyPlayState() doesn't reparse on
 * every render cycle (which fires on bootstrap poll, balance change, joining
 * flip, daily flip — up to a few times per minute).
 */
let cachedStarIconNode: SVGElement | null = null

function getCachedStarIconClone(): HTMLElement {
  if (cachedStarIconNode === null) {
    cachedStarIconNode = new DOMParser()
      .parseFromString(telegramStarIcon(14), 'image/svg+xml')
      .documentElement as unknown as SVGElement
  }
  const wrap = document.createElement('span')
  wrap.style.cssText = 'display:inline-flex; align-items:center;'
  wrap.appendChild(cachedStarIconNode.cloneNode(true))
  return wrap
}

function triggerHapticMedium(): void {
  try {
    const tg = window.Telegram?.WebApp as { HapticFeedback?: { impactOccurred?: (style: string) => void } } | undefined
    tg?.HapticFeedback?.impactOccurred?.('medium')
  } catch {
    // No-op outside Telegram.
  }
}

const MENU_KEYFRAMES_STYLE_ID = 'star-snake-menu-keyframes'

function ensureMenuKeyframes(): void {
  if (typeof document === 'undefined') return
  if (document.getElementById(MENU_KEYFRAMES_STYLE_ID)) return
  const style = document.createElement('style')
  style.id = MENU_KEYFRAMES_STYLE_ID
  style.textContent = `
    @keyframes star-snake-logo-glow {
      0%, 100% { text-shadow: 0 0 7px #fff, 0 0 15px #fbbf24, 0 0 30px #fbbf24, 0 0 50px #fbbf24, 0 0 80px #fbbf24; }
      50% { text-shadow: 0 0 12px #fff, 0 0 22px #fbbf24, 0 0 40px #fbbf24, 0 0 70px #fbbf24, 0 0 110px #fbbf24; }
    }
    @keyframes star-snake-play-breath {
      0%, 100% { transform: scale(1); }
      50% { transform: scale(1.015); }
    }
    @keyframes star-snake-daily-pulse {
      0%, 100% { box-shadow: 0 0 18px rgba(0,255,136,0.45); transform: scale(1); }
      50% { box-shadow: 0 0 32px rgba(0,255,136,0.75); transform: scale(1.06); }
    }
  `
  document.head.appendChild(style)
}
